// The labour-management UI gate, as one hook — and the SAME rule as the DB's
// can_manage_labour() (migs 20260706190000 / 20260811130000):
//
//   has_perm('labour','manage') OR has_perm('farm_labour','manage')
//     OR has_perm('harvest_workers','manage')
//
// has_perm resolves a live custom tenant role's map first, else the built-in
// role's defaults (role_permission_defaults), and passes everything for a
// platform admin in platform mode. get_my_permissions() returns exactly that
// resolved map, so `can()` over the same three keys is the same answer.
//
// It used to be `isManagerOrAbove(me.role) || can('labour','manage') ||
// can('farm_labour','manage')`. The enum role says nothing about a custom
// role's permissions — a custom role whose fallback_role is MANAGER but whose
// map lacks labour:manage passed here and was refused by every RPC and policy
// behind it — and harvest_workers:manage, which the DB accepts, was missing
// (review finding 3, 24 Sep 2026). With worker_special_info (race) gated on
// can_manage_labour(), a UI that disagrees with the DB would show a field the
// DB refuses, or hide one it allows. Built-in MANAGER / ADMIN / FARM_MANAGER
// hold `manage` on these keys by default, so they are unaffected.
//
// The one thing has_perm adds that this cannot see is a paired DEVICE's
// permission ceiling (device_permits) — a kiosk profile. There the DB is the
// stricter, which is the safe direction.
//
// Until the permission map has loaded this is false (fail closed); a caller
// that must not act on that interim value checks usePermissions().perms.

import { usePermissions } from '@/lib/usePermissions'
import { canFrom, type MyPermissions } from '@/lib/permissions'

/** The rule, pure — pinned by useCanManageLabour.test.ts. */
export function canManageLabourFrom(perms: MyPermissions | null | undefined): boolean {
  return canFrom(perms, 'labour', 'manage')
    || canFrom(perms, 'farm_labour', 'manage')
    || canFrom(perms, 'harvest_workers', 'manage')
}

export function useCanManageLabour(): boolean {
  const { perms } = usePermissions()
  return canManageLabourFrom(perms)
}
